import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Info } from 'lucide-react';
import { getProgram, MOVE_MODULES, ENROLL_URL } from '../data/programs';

const buildCurriculum = (type) => {
  if (type === 'modules') {
    return MOVE_MODULES.map((m) => ({
      label: `Module ${m.number}`,
      title: m.title,
      description: m.description,
    }));
  }
  if (type === 'sessions') {
    return MOVE_MODULES.map((m, i) => ({
      label: `Session ${i + 1}`,
      title: m.title,
      description: m.description,
    }));
  }
  if (type === 'weeks') {
    const weeks = [];
    MOVE_MODULES.forEach((m, i) => {
      weeks.push({
        label: `Week ${i * 2 + 1}`,
        title: `${m.title} · Part 1`,
        description: m.description,
      });
      weeks.push({
        label: `Week ${i * 2 + 2}`,
        title: `${m.title} · Part 2`,
        description: 'Deepening practice, integration, and pathway-specific application.',
      });
    });
    return weeks;
  }
  return [];
};

export default function ProgramDetail() {
  const { slug } = useParams();
  const program = getProgram(slug);

  if (!program) {
    return <Navigate to="/programs" replace />;
  }

  const curriculumItems = program.curriculum ? buildCurriculum(program.curriculum.type) : [];

  return (
    <div className="overflow-hidden" data-testid={`program-detail-${program.slug}`}>
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#FAFAF8] relative" data-testid="program-hero">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <Link
            to="/programs"
            className="inline-flex items-center gap-2 text-[#2D7D7B] hover:text-[#1F5A58] mb-8 transition-colors"
            data-testid="back-to-programs"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Programs
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
                {program.badge}
              </span>
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1B365D] mb-4">
                {program.name}
              </h1>
              <p className="text-lg font-medium text-[#2D7D7B] mb-6">{program.tagline}</p>
              <p className="text-lg text-[#1B365D]/70 leading-relaxed mb-8">{program.intro}</p>

              <div className="flex flex-wrap items-center gap-6">
                <a
                  href={ENROLL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="program-enroll-cta"
                >
                  {program.ctaLabel}
                  <ArrowRight className="w-5 h-5" />
                </a>
                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-3xl font-bold text-[#1B365D]">
                    {program.price}
                  </span>
                  <span className="text-sm text-[#1B365D]/60">{program.priceNote}</span>
                </div>
              </div>
            </div>

            {/* Program image */}
            <div className="relative w-full">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#C9A962]/20 to-transparent rounded-3xl" />
              <img
                src={program.image}
                alt={program.imageAlt}
                className="relative rounded-2xl shadow-2xl w-full h-[300px] md:h-[420px] object-cover"
                data-testid="program-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* At a Glance */}
      <section className="py-16 bg-white border-b border-[#C9A962]/15" data-testid="program-glance">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {program.glance.map((item) => (
              <div
                key={item.label}
                className="bg-[#F5F5F0] rounded-2xl p-6 border border-[#C9A962]/20"
              >
                <span className="text-xs font-bold uppercase tracking-wider text-[#C9A962]">
                  {item.label}
                </span>
                <p className="font-serif text-lg font-bold text-[#1B365D] mt-2 leading-snug">
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 md:py-32 bg-white" data-testid="program-includes">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
                What's Included
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1B365D] mb-6">
                Everything in Your Enrollment
              </h2>
              <p className="text-lg text-[#1B365D]/70 leading-relaxed">
                Built on the clinically grounded M.O.V.E.™ framework and tailored to your grief
                pathway for a personally resonant experience.
              </p>
            </div>

            <div className="bg-[#F5F5F0] rounded-2xl p-8">
              <ul className="space-y-4">
                {program.includes.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#2D7D7B] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#1B365D]">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Audience (optional) */}
      {program.audience && (
        <section className="py-24 bg-[#F5F5F0]" data-testid="program-audience">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
                {program.audienceTitle || "Who It's For"}
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1B365D]">
                Is This Right for You?
              </h2>
            </div>
            <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
              {program.audience.map((item, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl p-6 border border-[#C9A962]/20 flex items-start gap-3"
                >
                  <Check className="w-5 h-5 text-[#2D7D7B] flex-shrink-0 mt-0.5" />
                  <span className="text-[#1B365D]/80">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Curriculum (optional) */}
      {program.curriculum && (
        <section className="py-24 md:py-32 bg-white" data-testid="program-curriculum">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
                The Structure
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1B365D] mb-4">
                {program.curriculum.title}
              </h2>
              <p className="text-lg text-[#1B365D]/70">{program.curriculum.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {curriculumItems.map((item) => (
                <div
                  key={item.label}
                  className="bg-[#F5F5F0] rounded-2xl p-8 border border-[#C9A962]/20"
                >
                  <span className="text-xs font-bold uppercase tracking-wider text-[#C9A962]">
                    {item.label}
                  </span>
                  <h3 className="font-serif text-xl font-bold text-[#1B365D] mt-1 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-[#1B365D]/70 text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Note + CTA */}
      <section className="py-24 bg-[#1B365D] relative" data-testid="program-cta-section">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            {program.note && (
              <div className="inline-flex items-start gap-3 bg-white/5 backdrop-blur-sm rounded-xl px-6 py-4 border border-white/10 mb-8 text-left">
                <Info className="w-5 h-5 text-[#C9A962] flex-shrink-0 mt-0.5" />
                <span className="text-white/80">{program.note}</span>
              </div>
            )}
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Begin?
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Turning Pain Into Purpose — one structured step at a time.
            </p>
            <a
              href={ENROLL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#C9A962] text-[#1B365D] hover:bg-[#E0C585] px-8 py-4 rounded-full font-bold transition-colors inline-flex items-center justify-center gap-2"
              data-testid="program-final-cta"
            >
              {program.ctaLabel}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
